import { useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChangeBadge } from "@/components/invoices/InvoiceDetailDialog";
import api from "@/lib/api";
import { CATEGORIES, UNIT_OPTIONS, convertQty, eur, eurUnit, unitLabel, catLabel, currencySymbol } from "@/lib/format";
import { t } from "@/i18n";

const NEW = "__new__";
const toNum = (v) => parseFloat(String(v).replace(",", ".")) || 0;

function previewChange(line, ing) {
  if (!ing || !ing.current_unit_cost || !toNum(line.unit_price)) return null;
  const factor = convertQty(1, line.unit, ing.unit) || 1;
  const uc = toNum(line.unit_price) / factor;
  return ((uc - ing.current_unit_cost) / ing.current_unit_cost) * 100;
}

function ReviewLine({ line, idx, ingredients, onChange, onRemove }) {
  const ing = ingredients.find((i) => i.id === line.ingredient_id);
  const set = (patch) => onChange({ ...line, ...patch });
  const pick = (v) => {
    if (v === NEW) return set({ ingredient_id: null });
    const t = ingredients.find((i) => i.id === v);
    set({ ingredient_id: v, ingredient_name: t.name, category: t.category });
  };
  const change = previewChange(line, ing);

  return (
    <div className="rounded-xl border bg-background/50 p-3" data-testid={`review-line-${idx}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="truncate text-xs text-muted-foreground" title={line.raw_name}>{t("invoiceReviewDialog.facture")} {line.raw_name}</p>
        <div className="flex items-center gap-2">
          {ing ? <ChangeBadge value={change == null ? null : Math.round(change * 10) / 10} testId={`review-line-change-${idx}`} /> : <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">{t("common.nouvel_ingredient")}</span>}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove} data-testid={`review-line-remove-${idx}`} aria-label={t("invoiceReviewDialog.retirer_la_ligne")}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 md:col-span-4">
          <Select value={line.ingredient_id || NEW} onValueChange={pick}>
            <SelectTrigger data-testid={`review-line-ingredient-select-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW}>{t("invoiceReviewDialog.creer_un_nouvel_ingredient")}</SelectItem>
              {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({eurUnit(i.current_unit_cost)}/{unitLabel(i.unit)})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input className="num col-span-4 md:col-span-2" inputMode="decimal" value={line.quantity} onChange={(e) => set({ quantity: e.target.value, total_price: Math.round(toNum(e.target.value) * toNum(line.unit_price) * 100) / 100 })} aria-label={t("invoiceReviewDialog.quantite")} data-testid={`review-line-qty-${idx}`} />
        <div className="col-span-4 md:col-span-2">
          <Select value={line.unit} onValueChange={(v) => set({ unit: v })}>
            <SelectTrigger data-testid={`review-line-unit-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>{UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="relative col-span-4 md:col-span-2">
          <Input className="num pr-9" inputMode="decimal" value={line.unit_price} onChange={(e) => set({ unit_price: e.target.value, total_price: Math.round(toNum(e.target.value) * toNum(line.quantity) * 100) / 100 })} aria-label={t("invoiceReviewDialog.prix_unitaire")} data-testid={`review-line-price-${idx}`} />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">{currencySymbol()}/{unitLabel(line.unit)}</span>
        </div>
        <div className="relative col-span-12 md:col-span-2">
          <Input className="num pr-12" inputMode="decimal" value={line.total_price} onChange={(e) => set({ total_price: e.target.value })} aria-label={t("invoiceReviewDialog.total_ht")} data-testid={`review-line-total-${idx}`} />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">{t("invoiceReviewDialog.ht")}</span>
        </div>
        {!line.ingredient_id && (
          <>
            <Input className="col-span-12 md:col-span-8" value={line.ingredient_name} onChange={(e) => set({ ingredient_name: e.target.value })} placeholder={t("invoiceReviewDialog.nom_de_l_ingredient")} data-testid={`review-line-name-${idx}`} />
            <div className="col-span-12 md:col-span-4">
              <Select value={line.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger data-testid={`review-line-category-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </>
        )}
        {ing && <p className="col-span-12 text-[11px] text-muted-foreground">{t("invoiceReviewDialog.prix_actuel")} <span className="num">{eurUnit(ing.current_unit_cost)}/{unitLabel(ing.unit)}</span></p>}
      </div>
    </div>
  );
}

export default function InvoiceReviewDialog({ draft, onCancel, onConfirm, saving }) {
  const [form, setForm] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (!draft) return;
    setForm({ ...draft, lines: draft.lines.map((l, i) => ({ ...l, key: i })) });
    api.get("/ingredients").then(({ data }) => setIngredients(data));
  }, [draft]);

  const total = useMemo(() => (form?.lines || []).reduce((s, l) => s + toNum(l.total_price), 0), [form]);
  if (!form) return null;

  const setLine = (key, nl) => setForm({ ...form, lines: form.lines.map((l) => (l.key === key ? nl : l)) });
  const invalid = form.lines.length === 0 || form.lines.some((l) => !String(l.ingredient_name || "").trim());

  const submit = () => onConfirm({
    supplier_name: form.supplier_name, invoice_date: form.invoice_date, file_name: form.file_name,
    total_amount: toNum(form.total_amount),
    lines: form.lines.map(({ key, ...l }) => ({ ...l, quantity: toNum(l.quantity), unit_price: toNum(l.unit_price), total_price: toNum(l.total_price) })),
  });

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && !saving && onCancel()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="invoice-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("invoiceReviewDialog.verifier_la_facture")}</DialogTitle>
          <DialogDescription>{t("invoiceReviewDialog.corrigez_les_lignes_extraites_par_l_ia_les")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>{t("common.fournisseur")}</Label>
            <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} data-testid="review-supplier-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("invoiceReviewDialog.date_de_facture")}</Label>
            <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="review-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("invoiceReviewDialog.total_ht_2")}</Label>
            <Input className="num" inputMode="decimal" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="review-total-input" />
          </div>
        </div>
        <div className="space-y-2">
          {form.lines.map((l, i) => (
            <ReviewLine key={l.key} line={l} idx={i} ingredients={ingredients} onChange={(nl) => setLine(l.key, nl)}
              onRemove={() => setForm({ ...form, lines: form.lines.filter((x) => x.key !== l.key) })} />
          ))}
          {form.lines.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t("invoiceReviewDialog.aucune_ligne_a_importer")}</p>}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="review-lines-total">
          {form.lines.length} {t("invoiceReviewDialog.ligne_s_somme_des_lignes")} <span className="num">{eur(total)}</span>
          {Math.abs(total - toNum(form.total_amount)) > 0.05 && (
            <span className="ml-2 text-amber-600 dark:text-amber-400" data-testid="review-total-mismatch">
              {t("invoiceReviewDialog.total_saisi")}{eur(toNum(form.total_amount))}) ·{" "}
              <button type="button" className="font-semibold underline" onClick={() => setForm({ ...form, total_amount: Math.round(total * 100) / 100 })} data-testid="review-sync-total-btn">{t("invoiceReviewDialog.utiliser_la_somme")}</button>
            </span>
          )}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="review-cancel-btn">{t("common.annuler")}</Button>
          <Button onClick={submit} disabled={saving || invalid} data-testid="review-confirm-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}{t("invoiceReviewDialog.valider_et_mettre_a_jour_les_prix")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
